import React, { useContext, useState } from "react";
import {
	Box,
	Typography,
	TextField,
	Button,
	IconButton,
	Divider,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { CreatorContext } from "../../context/CreatorContext";
import { useTranslation } from "react-i18next";
import { EyeOutline } from "react-ionicons";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import WeekCalendar from "../WeekCalendar/WeekCalendar";

const ShowcaseCalendar = ({
	calendarBackgroundColor,
	backgroundColor,
	calendar,
	showAgenda,
}) => {
	const { creator } = useContext(CreatorContext);
	const { t } = useTranslation();
	const [date, setDate] = useState({ date: new Date() });

	const onChange = (date) => setDate({ date });

	function hexToRGBA(hex, opacity) {
		let r = parseInt(hex.slice(1, 3), 16);
		let g = parseInt(hex.slice(3, 5), 16);
		let b = parseInt(hex.slice(5, 7), 16);

		return `rgba(${r}, ${g}, ${b}, ${opacity})`;
	}

	const getTextColorBasedOnBackground = (bgColor) => {
		const r = parseInt(bgColor.substring(1, 3), 16);
		const g = parseInt(bgColor.substring(3, 5), 16);
		const b = parseInt(bgColor.substring(5, 7), 16);
		const brightness = (r * 299 + g * 587 + b * 114) / 1000;
		return brightness > 128 ? "#000000" : "#FFFFFF";
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				paddingTop: 0,
				height: "100%",
				"& *": {
					fontFamily: creator.fontFamilyCap + "!important",
				},
				"& .react-calendar": {
					border: "none",
					fontFamily: creator.fontFamilyCap + "!important",
					fontSize: 9,
					background: "transparent",
				},
				"& .react-calendar abbr": {
					textDecoration: "none!important",
				},
				"& .react-calendar__navigation": {
					fontSize: 9,
					fontFamily: creator.fontFamilyCap + "!important",
					height: "20px!important",
					marginBottom: "0px!important",
					color: hexToRGBA(
						getTextColorBasedOnBackground(calendarBackgroundColor || "#ffffff"),
						getTextColorBasedOnBackground(
							calendarBackgroundColor || "#ffffff"
						).toLowerCase() === "#ffffff"
							? 0.4
							: 0.5
					), //, "#999999",
				},
				"& .react-calendar__navigation__label": {
					fontSize: "9px!important",
					fontWeight: 400,
					color: hexToRGBA(
						getTextColorBasedOnBackground(calendarBackgroundColor || "#ffffff"),
						getTextColorBasedOnBackground(
							calendarBackgroundColor || "#ffffff"
						).toLowerCase() === "#ffffff"
							? 0.3
							: 0.3
					), //"#757575",
				},
				"& .react-calendar__navigation button": {
					minWidth: "20px!important",
				},
				"& .react-calendar__navigation__prev2-button, & .react-calendar__navigation__next2-button":
					{
						display: "none",
					},
				"& .react-calendar__navigation__prev-button, & .react-calendar__navigation__next-button":
					{
						color: creator.color,
					},
				"& .react-calendar__tile": {
					padding: "7.58px!important",
					paddingTop: "2.9px !important",
					paddingBottom: "2.9px !important",
					color: hexToRGBA(
						getTextColorBasedOnBackground(calendarBackgroundColor || "#ffffff"),
						getTextColorBasedOnBackground(
							calendarBackgroundColor || "#ffffff"
						).toLowerCase() === "#ffffff"
							? 0.4
							: 0.6
					), //"#4a4a4a",
				},
				"& .react-calendar__month-view__weekdays": {
					textTransform: "capitalize",
					color: hexToRGBA(
						getTextColorBasedOnBackground(calendarBackgroundColor || "#ffffff"),
						getTextColorBasedOnBackground(
							calendarBackgroundColor || "#ffffff"
						).toLowerCase() === "#ffffff"
							? 0.1
							: 0.9
					), //"#757575",
				},
				"& .react-calendar__tile--active, & .react-calendar__tile--now": {
					backgroundColor: "transparent !important",
				},

				"& .react-calendar__tile--now abbr": {
					backgroundColor: "white!important",
					color: creator.color + "!important",
				},
				"& abbr": {
					display: "block",
				},
				"& .react-calendar__month-view__days__day abbr": {
					width: 16,
					height: 16,
				},
				"& .react-calendar__tile--active abbr": {
					backgroundColor: creator.color + "!important",
					color: "white !important",
					padding: "4px",
					borderRadius: "50%",
					fontSize: "6px",
					width: 16,
					height: 16,
					marginTop: "-6px",
				},
				"& .react-calendar__month-view__days__day:hover": {
					background: "transparent!important",
				},
				"& .react-calendar__month-view__days__day--neighboringMonth": {
					opacity: 0.6,
				},
				"& .react-calendar__navigation button:enabled:hover, .react-calendar__navigation button:enabled:focus":
					{
						background: "transparent!important",
					},
			}}
		>
			<Box sx={{ padding: "0px 5px", background: calendarBackgroundColor }}>
				{!showAgenda ? (
					<Calendar onChange={onChange} value={date.date} />
				) : (
					<WeekCalendar
						calendarBackgroundColor={calendarBackgroundColor}
						backgroundColor={backgroundColor}
					/>
				)}
			</Box>
			<Box
				sx={{
					flex: 1,
					display: "flex",
					backgroundColor: backgroundColor,
					padding: 1,
					alignItems: "center",
					flexDirection: calendar === "calendar2" ? "row" : "column",
					flexWrap: calendar === "calendar2" ? "wrap" : "nowrap",
					gap: calendar === "calendar1" ? 1.5 : 2,
					overflow: "auto",
				}}
			>
				{[
					"09:00",
					"09:30",
					"10:00",
					"10:30",
					"11:00",
					"11:30",
					"12:00",
					"12:30",
					"13:00",
					"14:00",
					"14:30",
					"15:00",
					"15:30",
					"16:00",
					"16:30",
				].map((hour) => (
					<Box
						key={hour}
						sx={{
							width: calendar === "calendar3" ? 100 : 50,
							borderRadius: "8px",
							border:
								calendar === "calendar1"
									? "none"
									: "1px solid " +
									  hexToRGBA(
											getTextColorBasedOnBackground(
												backgroundColor || "#ffffff"
											),
											getTextColorBasedOnBackground(
												backgroundColor || "#ffffff"
											).toLowerCase() === "#ffffff"
												? 0.2
												: 0.1
									  ),
							fontSize: 7,
							padding: 0.5,
							textAlign: "center",
							color: getTextColorBasedOnBackground(
								backgroundColor || "#ffffff"
							),
						}}
					>
						{hour}
					</Box>
				))}
			</Box>
		</Box>
	);
};

export default ShowcaseCalendar;
